
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useToast } from '../../components/common/ToastContext';
import { WorkflowExecutionLog, Workflow } from '@repo/shared-types';
import { WorkflowTriggerData } from '@repo/shared-types/workflow';
import { workflowSchema, workflowExecutionLogSchema } from '@repo/shared-types/workflow/schemas';
import { validateEntityArray } from '@repo/shared-utils';
import { logger } from '@repo/shared-utils';

export const useWorkflows = () => {
    return useQuery({
        queryKey: ['workflows'],
        queryFn: async () => {
            const data = await api.workflows.list();
            // ✅ Validate query response with Zod schema
            const validation = validateEntityArray(workflowSchema, data);
            if (!validation.valid) {
                logger.error('Invalid workflows query response:', validation.errors);
                throw new Error('Invalid workflows data received from API');
            }
            return validation.data! as Workflow[];
        },
    });
};

export const useUpdateWorkflow = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: ({ id, updates }: { id: string; updates: Partial<Workflow> }) => api.workflows.update(id, updates),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['workflows'] });
            if (data.status === 'Active') showToast('Workflow activated', 'success');
            else if (data.status === 'Paused') showToast('Workflow paused', 'info');
        }
    });
};

export const useExecutionLogs = () => {
    return useQuery({
        queryKey: ['workflows', 'logs'],
        queryFn: async () => {
            const data = await api.workflows.logs.list();
            // ✅ Validate query response with Zod schema
            const validation = validateEntityArray(workflowExecutionLogSchema, data);
            if (!validation.valid) {
                logger.error('Invalid workflow execution logs query response:', validation.errors);
                throw new Error('Invalid workflow execution logs data received from API');
            }
            return validation.data! as WorkflowExecutionLog[];
        },
        refetchInterval: 1000 * 10 // Poll every 10s for updates
    });
};

export const useCreateExecutionLog = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (log: WorkflowExecutionLog) => api.workflows.logs.create(log),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workflows', 'logs'] });
        }
    });
};

export const useCreateWorkflow = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (workflow: Workflow) => api.workflows.create(workflow),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workflows'] });
            showToast('Workflow created successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to create workflow', 'error');
        }
    });
};

export const useDeleteWorkflow = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (id: string) => api.workflows.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workflows'] });
            showToast('Workflow deleted successfully', 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to delete workflow', 'error');
        }
    });
};

export const useExecuteWorkflow = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: ({ workflowId, triggerData }: { workflowId: string; triggerData?: WorkflowTriggerData }) =>
            api.workflows.execute(workflowId, triggerData),
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ['workflows'] });
            queryClient.invalidateQueries({ queryKey: ['workflows', 'logs'] });
            if (result.success) {
                showToast('Workflow executed successfully', 'success');
            } else {
                showToast(result.error || 'Workflow execution failed', 'error');
            }
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to execute workflow', 'error');
        }
    });
};

export const useRunAllWorkflows = () => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: () => api.workflows.runAll(),
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ['workflows'] });
            queryClient.invalidateQueries({ queryKey: ['workflows', 'logs'] });
            showToast(`Automation Run Complete. ${result.executed} workflows executed, ${result.failed} failed.`, 'success');
        },
        onError: (error: Error) => {
            showToast(error.message || 'Failed to run workflows', 'error');
        }
    });
};


